// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Repeats the track content until it covers the viewport twice for a gapless loop, and
// animates with the Web Animations API. Clones are hidden from assistive tech.
// Respects prefers-reduced-motion (no animation), pauses on hover/focus, and toggles on
// the `--toggle-pause` command from a commandfor button. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #userPaused = false;
  #originals = null;
  #resizeTimer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal });
    this.addEventListener("mouseleave", () => this.#resume(), { signal });
    this.addEventListener("focusin", () => this.#animation?.pause(), { signal });
    this.addEventListener("focusout", () => this.#resume(), { signal });
    this.addEventListener("command", (event) => {
      if (event.command === "--toggle-pause") this.#togglePause();
    }, { signal });

    window.addEventListener("resize", () => {
      clearTimeout(this.#resizeTimer);
      this.#resizeTimer = setTimeout(() => this.#setup(), 200);
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#resizeTimer);
    this.#animation?.cancel();
  }

  #resume() {
    if (!this.#userPaused) this.#animation?.play();
  }

  #togglePause() {
    this.#userPaused = !this.#userPaused;
    if (this.#userPaused) this.#animation?.pause();
    else this.#animation?.play();
    const button = this.querySelector("[data-marquee-toggle]");
    if (button) {
      button.setAttribute("aria-pressed", String(this.#userPaused));
      button.setAttribute("aria-label", this.#userPaused ? button.dataset.labelPlay : button.dataset.labelPause);
    }
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    // Remember the authored content once, and reset to it on every setup.
    if (!this.#originals) this.#originals = [...track.children];
    track.replaceChildren(...this.#originals);

    if (this.#mql.matches) return;

    const unit = track.scrollWidth;
    if (unit <= 0) return;

    // One "set" must be at least as wide as the viewport; the track holds two sets.
    const viewport = Math.max(this.clientWidth, window.innerWidth);
    const repeats = Math.max(1, Math.ceil(viewport / unit));
    const clones = [];
    for (let set = 0; set < repeats * 2 - 1; set++) {
      for (const node of this.#originals) {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.removeAttribute("id");
        clones.push(clone);
      }
    }
    track.append(...clones);

    const distance = track.scrollWidth / 2;
    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    if (this.#userPaused) this.#animation.pause();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
